import type { ReadonlyURLSearchParams } from 'next/navigation';

import {
    FILTER_TYPE_LOCATION,
    FILTER_TYPE_PRICE,
    FILTER_TYPE_REVIEWS,
    SHOP_BY_PREFIX,
} from '@/components/Analytics/constants';
import {
    GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE,
    SEARCH_SERVICE_PROVIDERS,
} from '@/constants/grs';
import {
    BOPIM_PICK_UP_TEXT_KEY,
    ITEM_CATEGORY_FACET_KEY,
    ITEM_LOCATION_BOPIM_KEY,
    ITEM_LOCATION_BOPIW,
    ITEM_LOCATION_FACET_KEY,
    ITEM_LOCATION_PRICING_FACET_KEY,
    ITEM_PROGRAM_FACET_KEY,
    ITEM_RATING_FACET_KEY,
    REFINEMENT_KEY_DELIVERY,
    REFINEMENT_KEY_IN_STOCK,
    REFINE_QUERY_DELIMITER,
} from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import { isBDSite } from '@/services/search/utils';
import { GRS_USE_WAREHOUSE_INVENTORY_BUCKET_VALUE } from '@/src/components/SearchResultsFacets/grs.constants';
import { checkGRSRefinementApplied } from '@/src/components/SearchResultsFacets/grs.lib';
import type { ContentStackEntryDataProps } from '@/types/contentStack';
import type { Facet, SearchResultLW } from '@/types/searchQuery';
import { getRatingsLabel } from '@/utils/searchFacets';

import {
    BUY_IN_WAREHOUSE_TEXT_KEY,
    CURRENT_PAGE,
    DELIVERY_BUCKET_VALUE,
    DELIVERY_TEXT_KEY,
    HIDE_OUT_OF_STOCK_TEXT_KEY,
    IN_WAREHOUSE_BUCKET_VALUE,
    OUT_OF_STOCK_BUCKET_VALUE,
    OUT_OF_STOCK_TEXT_KEY,
    PICK_UP_TEXT_KEY,
    REFINE_QUERY_PARAM,
    TWO_DAY_DELIVERY_BUCKET_VALUE,
    TWO_DAY_DELIVERY_TEXT_KEY,
} from './constants';
import type { FacetBucketNormalized, FacetNormalized } from './types';

function isEncoded(uri?: string | null) {
    uri = uri || '';

    return uri !== decodeURIComponent(uri);
}

export function fullyDecodeURI(uri?: string | null) {
    if (!uri) return;

    /**
     * Due to an unfortunate complication with Legacy refinements being double encoded and + symbols having complex interactions with space characters,
     * the following logic helps us fix legacy URLs. Currently we identify legacy refinements by them starting with ||, which we check after our first decoding
     * (which is done by toStringing search params which happens at some point before this method is called) to catch the possibility
     * that the | character is encoded.
     **/

    // If we are a legacy URL
    if (uri.startsWith('||')) {
        /**
         * Replace + characters that now exist after initial decode to space characters
         * Actual + characters would have been encoded to %252B, and would now be %2B. Hence, this only catches what legacy wanted to be space characters
         * */
        uri = uri.replace(/\+/g, ' ');
        // Now let decoding happen to get to actual spaces/decode other non-issue double encoded characters
    }

    while (isEncoded(uri)) {
        uri = decodeURIComponent(uri);
    }

    return uri;
}

/** true if the hide out of stock facet filter should be shown and be disabled by default instead of showing "show out of stock" and enabled by default */
export function isEnableHideOutOfStockFilter(
    config: ContentStackEntryDataProps | undefined,
): boolean {
    if (!config) return false;
    return !!(
        Array.isArray(config.properties) &&
        config.properties[0] &&
        typeof config.properties[0] === 'object' &&
        'enableHideOutOfStockFilter' in config.properties[0] &&
        (
            config.properties[0] as {
                enableHideOutOfStockFilter?: boolean;
            }
        ).enableHideOutOfStockFilter
    );
}

export function getFacetLabel(
    bucketValue: string,
    config: ContentStackEntryDataProps | undefined,
    searchService?: SearchServiceProps,
) {
    if (!config) {
        console.warn('Missing facet label config');
        return null;
    }

    switch (bucketValue.toLowerCase()) {
        case DELIVERY_BUCKET_VALUE:
            return getKeyValue(DELIVERY_TEXT_KEY, config);
        case TWO_DAY_DELIVERY_BUCKET_VALUE:
            return getKeyValue(TWO_DAY_DELIVERY_TEXT_KEY, config);
        case OUT_OF_STOCK_BUCKET_VALUE:
            if (isEnableHideOutOfStockFilter(config))
                return getKeyValue(HIDE_OUT_OF_STOCK_TEXT_KEY, config);
            return getKeyValue(OUT_OF_STOCK_TEXT_KEY, config);
        case PICK_UP_TEXT_KEY:
            return getKeyValue(PICK_UP_TEXT_KEY, config);
        case GRS_USE_WAREHOUSE_INVENTORY_BUCKET_VALUE:
            return searchService === GRS_SEARCH_PROVIDER_QUERY_PARAM_VALUE
                ? getKeyValue(PICK_UP_TEXT_KEY, config)
                : null;
        case IN_WAREHOUSE_BUCKET_VALUE:
            return getKeyValue(BUY_IN_WAREHOUSE_TEXT_KEY, config);
        case ITEM_LOCATION_BOPIM_KEY:
            return getKeyValue(BOPIM_PICK_UP_TEXT_KEY, config);
        default:
            return null;
    }
}

/**
 * Function to identify the filterType from the filterKey
 * @param filterKey
 * @returns filterType
 */
export function getFilterType(filterKey: string) {
    let filterType;
    if (filterKey.includes('_attr')) {
        filterType = filterKey.split('_attr')[0]?.replace('_', ' ');
    }
    return filterType ? SHOP_BY_PREFIX + filterType : '';
}

/**
 * Even though Lucidworks returns a display order, they are not in the order the business
 * wants them, so we need to remap the the top few groups.... :shrug:
 */
export function remapFacetDisplayOrder(facet: FacetNormalized) {
    if (facet.facetKey === ITEM_CATEGORY_FACET_KEY) {
        facet.display_order = 0;
    }
    if (facet.facetKey === ITEM_PROGRAM_FACET_KEY) {
        facet.display_order = 1;
    }
    if (facet.facetKey === ITEM_LOCATION_BOPIW) {
        facet.display_order = 3;
    }
    if (facet.facetKey === ITEM_LOCATION_FACET_KEY) {
        facet.display_order = 4;
    }
    return facet;
}

export function sortFacets(a: FacetNormalized, b: FacetNormalized) {
    return (a.display_order ?? 0) - (b.display_order ?? 0);
}

export function modifyRangeString(input: string): string {
    // Use a regex to match the pattern "<digits>+to+<digits>"
    const regex = /\[?(\d+)[\+\s]?to[\+\s]?(\d+)\]?/gi;

    // Replace matches with the desired format "[<digits> TO <digits>]"
    return input.replace(regex, (_, start, end) => `[${start} TO ${end}]`);
}

function formatDollarPrice(price: string | number) {
    return parseInt(price.toString()).toLocaleString(undefined, {
        style: 'currency',
        currency: 'usd',
        maximumFractionDigits: 0,
    });
}

export function parseRefineParamToLabels(
    refinementString: string | string[] | undefined,
    config?: ContentStackEntryDataProps,
) {
    if (!refinementString) {
        return [];
    }

    if (Array.isArray(refinementString)) {
        refinementString = refinementString[0] as string;
    }

    return refinementString.split(REFINE_QUERY_DELIMITER).map(refinement => {
        let refinementToUse = refinement;
        let [key, value] = refinementToUse.split(/-(.*)/s);

        // Hack to support legacy link with Delivery_Type_attr-Warehouse%2BPick-Up
        if (key?.toLowerCase() === 'delivery_type_attr') {
            value = value?.replace(/\+/gi, ' ');
        }

        if (!key || !value) {
            return { label: '', filterType: '', refinementToUse };
        }

        let label = value;
        let filterType = '';

        switch (key.toLowerCase()) {
            case ITEM_LOCATION_PRICING_FACET_KEY.toLowerCase():
                // Returns like [1000 TO 2000]
                refinementToUse = modifyRangeString(refinementToUse);
                const [lowPrice, highPrice] = value.match(/([\d]+)/g) || [];

                if (lowPrice && highPrice) {
                    label = `${formatDollarPrice(lowPrice)} to ${formatDollarPrice(highPrice)}`;
                }
                filterType = FILTER_TYPE_PRICE;
                break;
            case ITEM_CATEGORY_FACET_KEY:
                const nesting = value.split('|');
                label = nesting[nesting.length - 1]!;
                break;
            case ITEM_RATING_FACET_KEY:
                label = getRatingsLabel(value);
                filterType = FILTER_TYPE_REVIEWS;
                break;
            case ITEM_LOCATION_FACET_KEY:
                if (isEnableHideOutOfStockFilter(config)) {
                    label = getFacetLabel(value, config)!;
                    filterType = FILTER_TYPE_LOCATION;
                } else {
                    // In stock checkbox is inverse of value
                    label = '';
                }
                break;
            case ITEM_PROGRAM_FACET_KEY:
                label = getFacetLabel(value, config)!;
                filterType = FILTER_TYPE_LOCATION;
                break;
            case ITEM_LOCATION_BOPIW:
                label = getFacetLabel(PICK_UP_TEXT_KEY, config)!;
                filterType = FILTER_TYPE_LOCATION;
                break;
            case ITEM_LOCATION_BOPIM_KEY:
                label = getFacetLabel(ITEM_LOCATION_BOPIM_KEY, config)!;
                filterType = FILTER_TYPE_LOCATION;
                break;
            default:
                label = value;
                filterType = getFilterType(key);
                break;
        }

        return { label, filterType, refinement: refinementToUse };
    });
}

export function getSelectLWFacetsFromUrl(
    searchParams: URLSearchParams,
    config: ContentStackEntryDataProps,
    resultFacets: SearchResultLW['facets'],
) {
    const refinements = parseRefineParamToLabels(
        fullyDecodeURI(searchParams.get(REFINE_QUERY_PARAM)) || '',
        config,
    );

    // Add filters that are opt-out
    // deliveryFacetFlag could either be omitted, or set true or false, if true, it will be added
    // via the normal parsing, so if we see it at all, do nothing
    if (!searchParams.has('refine')) {
        // Some results wont have the default filters applicable, make sure they are in the response
        if (checkIfFacetValueExists(resultFacets, REFINEMENT_KEY_DELIVERY)) {
            refinements.push({
                label: getFacetLabel(DELIVERY_BUCKET_VALUE, config)!,
                filterType: FILTER_TYPE_LOCATION,
                refinement: REFINEMENT_KEY_DELIVERY,
            });
        }
    }

    if (!isEnableHideOutOfStockFilter(config)) {
        const refineQueryParam =
            fullyDecodeURI(searchParams.get(REFINE_QUERY_PARAM)) || '';
        const inStockOnlyRefinement = refineQueryParam
            .split(REFINE_QUERY_DELIMITER)
            .find(refinement => refinement === REFINEMENT_KEY_IN_STOCK);

        // If not in stock only, add the show out of stock as long as it exits in the returned facets
        if (!inStockOnlyRefinement) {
            if (
                checkIfFacetValueExists(resultFacets, REFINEMENT_KEY_IN_STOCK)
            ) {
                refinements.push({
                    label: getFacetLabel('in stock', config)!,
                    filterType: FILTER_TYPE_LOCATION,
                    refinement: REFINEMENT_KEY_IN_STOCK,
                });
            }
        }
    }
    return refinements.filter(({ label }) => label);
}

export function isNonDefaultFacetApplied(searchParams: URLSearchParams) {
    const refineQueryParam = fullyDecodeURI(
        searchParams.get(REFINE_QUERY_PARAM),
    );

    const refinementArray =
        refineQueryParam?.split(REFINE_QUERY_DELIMITER) || [];
    const nonDefaultRefinementArray = refinementArray.filter(
        refinement => refinement !== REFINEMENT_KEY_DELIVERY,
    );

    return nonDefaultRefinementArray.length > 0;
}

export function checkIfFacetValueExists(
    facetList: Facet[],
    refinement: string,
) {
    const [facetKey, val] = refinement.split(/-(.*)/s);
    const facet = facetList?.find(group => group.facetKey === facetKey);

    if (!facet) {
        return false;
    }

    if (facet.buckets.find(bucket => bucket.val === val)) {
        return true;
    }

    return false;
}

export function checkRefinementApplied({
    facet,
    bucket,
    searchParams,
    searchService,
    config,
}: {
    facet: FacetNormalized;
    bucket: FacetBucketNormalized;
    searchParams: ReadonlyURLSearchParams;
    searchService: SearchServiceProps;
    config?: ContentStackEntryDataProps;
}): boolean {
    if (searchService === SEARCH_SERVICE_PROVIDERS.LUCIDWORKS) {
        if (facet.facetKey === ITEM_LOCATION_PRICING_FACET_KEY) {
            return checkLWRefinementApplied(
                `${facet.facetKey}-[${bucket.low} TO ${bucket.high}]`,
                searchParams,
            );
        } else {
            return checkLWRefinementApplied(
                `${facet.facetKey}-${bucket.val}`,
                searchParams,
                undefined,
                config,
            );
        }
    } else if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        return checkGRSRefinementApplied({
            facet,
            bucket,
            searchParams,
        });
    } else {
        return false;
    }
}

export function checkLWRefinementApplied(
    key: string,
    searchParams: ReadonlyURLSearchParams,
    modifyRefinement?: (refinement: string) => string,
    config?: ContentStackEntryDataProps,
): boolean {
    // Delivery only and possibly In Stock are applied if refinement param is not set
    if (!searchParams.has(REFINE_QUERY_PARAM)) {
        if (key === REFINEMENT_KEY_DELIVERY) {
            return true;
        }
        if (
            !isEnableHideOutOfStockFilter(config) &&
            key === REFINEMENT_KEY_IN_STOCK
        ) {
            return true;
        }
    }

    let refine = fullyDecodeURI(searchParams.get(REFINE_QUERY_PARAM));

    if (modifyRefinement) {
        refine = modifyRefinement(refine || '');
    }

    const inUrl = refine?.split(REFINE_QUERY_DELIMITER).includes(key);

    // If the "hide out of stock" filter is not available and the "only in stock" filter is present, the checkbox should be unchecked
    if (
        !isEnableHideOutOfStockFilter(config) &&
        key === REFINEMENT_KEY_IN_STOCK
    ) {
        return !inUrl;
    }

    return !!inUrl;
}

export function updateSearchParams(
    updateSearchParamsProvider: (queryParams: ReadonlyURLSearchParams) => void,
    searchParams: ReadonlyURLSearchParams | URLSearchParams,
) {
    const queryParams = new URLSearchParams(searchParams);

    // Use the history API to update the URL without reloading the page
    window.history.pushState(
        {},
        '',
        `${window.location.pathname}?${queryParams.toString()}`,
    );
    window.scrollTo(0, 0);
    updateSearchParamsProvider(queryParams as ReadonlyURLSearchParams);
}

/**
 * Takes a facet key and value, adds or removes it, and returns the new query string
 */
export function updateLWRefinements(
    updateSearchParamsProvider: (queryParams: ReadonlyURLSearchParams) => void,
    refinementPair: string,
    isEnabled: boolean,
    urlParams: ReadonlyURLSearchParams,
    config?: ContentStackEntryDataProps,
) {
    const queryParams = new URLSearchParams(urlParams);

    // Remove pagination page counter when applying filters to avoid ending up no search results
    queryParams.delete(CURRENT_PAGE);

    let refinementArray =
        fullyDecodeURI(queryParams.get(REFINE_QUERY_PARAM))?.split(
            REFINE_QUERY_DELIMITER,
        ) || [];

    // To help prevent duplicates, always filter out the current key
    refinementArray = refinementArray.filter(refinement => {
        const refinementToCompare = modifyRangeString(`${refinement}`);
        return refinementToCompare !== refinementPair;
    });

    // Some options are defaulted to on when there is no refinement array. So
    // if we are going from a state with no refinements to a state with refinements param,
    // we need to add the default refinements
    // Do not default to shipit for BD.
    const defaultRefinements = isBDSite(process.env.NEXT_PUBLIC_SITE)
        ? []
        : [REFINEMENT_KEY_DELIVERY];

    // Now add the new one if it is enabled
    if (isEnabled) {
        // Add refinement if "hide out of stock" filter is available,
        // or if it's not the in-stock refinement when "hide out of stock" filter is not available
        if (
            isEnableHideOutOfStockFilter(config) ||
            refinementPair !== REFINEMENT_KEY_IN_STOCK
        ) {
            refinementArray.push(refinementPair);
        }
    }

    // If turning off a defaulted refinement, add all the other ones
    // if there wasn't a previous refinement param, add the default ones
    // or they wont be applied
    if (
        !isEnabled &&
        defaultRefinements.includes(refinementPair) &&
        !queryParams.has(REFINE_QUERY_PARAM)
    ) {
        const otherDefaults = defaultRefinements.filter(
            r => r !== refinementPair,
        );

        for (const refinement of otherDefaults) {
            if (!refinementArray.includes(refinement)) {
                refinementArray.push(refinement);
            }
        }
    }

    // If "hide out of stock" filter is not available, and "removing" show out of stock, then add in stock only
    if (
        !isEnableHideOutOfStockFilter(config) &&
        !isEnabled &&
        refinementPair === REFINEMENT_KEY_IN_STOCK
    ) {
        refinementArray.push(refinementPair);
    }

    // If we're adding any other refinement, add all defaults
    if (
        !queryParams.has(REFINE_QUERY_PARAM) &&
        refinementPair !== REFINEMENT_KEY_DELIVERY
    ) {
        refinementArray.push(...defaultRefinements);
    }

    // Note: We always need to set the refine key, even if the array is empty as
    // the empty refinement key is needed to turn off the REFINEMENT_KEY_DELIVERY default refinement
    queryParams.set(
        REFINE_QUERY_PARAM,
        encodeURIComponent(refinementArray.join(REFINE_QUERY_DELIMITER)),
    );

    // On refinement change, back to page one
    queryParams.delete('page');

    updateSearchParams(updateSearchParamsProvider, queryParams);
}

export function updatePageParam(
    updateSearchParamsProvider: (queryParams: ReadonlyURLSearchParams) => void,
    searchParams: ReadonlyURLSearchParams,
    pageNumber: string,
) {
    const queryParams = new URLSearchParams(searchParams);
    queryParams.set('currentPage', pageNumber);
    updateSearchParams(updateSearchParamsProvider, queryParams);
}
